// We can only import types here, otherwise we'll fail builds due to SSR
import { ProductGame, PurchaseStatus } from "@/@types/common";
import { VirtualCurrencyCode } from "@/@types/currencies";
import { PurchaseData } from "@/@types/purchase";
import { useModal } from "@/providers/modal";
import { CheckoutState, useCheckout } from "@/providers/useCheckout";
import { useCreator } from "@/providers/useCreator";
import { useStoredSessions } from "@/providers/useStoredSessions";
import { useSimpleI18n } from "@/utils/rioti18n";
import { safeSessionStorage } from "@/utils/safeStorages";
import siteConfig from "@/variables";
import { navigate } from "gatsby";
import React from "react";
import logger from "../utils/logger";
import { useGame } from "@/providers/useGame";
import { useXSSOAuth } from "./useXSSOAuth";
import { StartOrderV3Response } from "@/@types/data/orderV3";
import { purchaseVcBundle, startOrder } from "@/data/ordersV3";

export type ActionsState = {
  performLoginAction: CallableFunction;
  saveLoginAction: CallableFunction;
};

export type Action = {
  type: ActionType;
};

export type ActionData = PurchaseActionData | SuccessActionData | RedirectActionData;

export type PurchaseActionData = {
  autobuyProduct: object;
  purchaseData: PurchaseData;
};

export type SuccessActionData = {
  successPath: string;
  successHash: string;
};

export type RedirectActionData = {
  redirectPath: string;
};

export enum ActionType {
  PURCHASE_PRODUCT = "PURCHASE_PRODUCT",
  PURCHASE_VC_BUNDLE = "PURCHASE_VC_BUNDLE",
  SUCCESS_PAGE = "SUCCESS_PAGE",
  REDIRECT = "REDIRECT",
  NO_ACTION = "NO_ACTION",
}

const EMPTY_AUTH_STATE: ActionsState = {
  performLoginAction: () => Boolean,
  saveLoginAction: () => void 0,
};

const context = React.createContext<ActionsState>(EMPTY_AUTH_STATE);

export const useLoginActions = () => React.useContext(context);

const afterLoginPurchase = async (
  actionData: PurchaseActionData,
  checkout: CheckoutState,
  language: string,
  trafficSource: string,
  storedSessions: { getValues: CallableFunction; update: CallableFunction },
  i18Navigate: CallableFunction,
  affiliateName?: string,
  isLoggedIn?: boolean,
) => {
  // If no purchase data then return early
  if (!(actionData.autobuyProduct && actionData.purchaseData && !isLoggedIn)) {
    return false;
  }

  try {
    const { error, status, context }: StartOrderV3Response = await startOrder(
      language,
      actionData.purchaseData,
      trafficSource,
      storedSessions.getValues(),
    );
    if (!error || !error?.status) {
      if (status === PurchaseStatus.ORDER_CONFIRMATION_REQUIRED) {
        const balance = context?.player_balance?.find((b) => b.currency === VirtualCurrencyCode.RP)?.amount ?? 0;
        checkout.setBalance(balance ?? 0);
        checkout.setAffiliate(affiliateName ?? "");
        checkout.setProducts(actionData.purchaseData.products);
        // If we use navigate here we unmount the riotbar footer and its not replaced again once the page is loaded.
        // By triggering a full page change we force the riotbar to reload, which causes the footer to load.
        i18Navigate(`/checkout`, { replace: true });
        return true;
      }

      if (context?.redirect_url) {
        context.redirect_url.indexOf("://")
          ? (window.location.href = context.redirect_url)
          : i18Navigate(context.redirect_url, { replace: true });
        return true;
      }
    }
  } catch (e) {
    // Just logging the error and continuing, this would allow the player to continue to see the modal as its loaded on the product screen.
    logger("An error occurred while trying to startOrder from login", e as Error, "error");
  }

  return false;
};

export const LoginActionsProvider = ({ children }: { children?: React.ReactNode }) => {
  // i18n isn't available for use, so we need to use a simple recreation.
  const { language, navigate: i18nNavigate } = useSimpleI18n();
  const { isLoggedIn, loading, login, logout } = useXSSOAuth();
  const { trafficSource, creator: { name } = {} } = useCreator(undefined, true);
  const checkout = useCheckout();
  const modal = useModal();
  const storedSessions = useStoredSessions();
  const gameId: ProductGame = useGame();

  const saveLoginAction = React.useCallback((type: ActionType, data?: ActionData) => {
    safeSessionStorage.setItem(siteConfig.KEY_LOGIN_ACTION_TYPE, type);
    safeSessionStorage.setItem(siteConfig.KEY_LOGIN_ACTION_DATA, JSON.stringify(data ?? {}));
  }, []);

  const performLoginAction = React.useCallback(async () => {
    const actionTypeStorage = safeSessionStorage.getItem(siteConfig.KEY_LOGIN_ACTION_TYPE);
    safeSessionStorage.removeItem(siteConfig.KEY_LOGIN_ACTION_TYPE);
    const actionType: ActionType = actionTypeStorage
      ? ActionType[actionTypeStorage as keyof typeof ActionType]
      : ActionType.NO_ACTION;
    const actionDataJson = safeSessionStorage.getItem(siteConfig.KEY_LOGIN_ACTION_DATA);
    safeSessionStorage.removeItem(siteConfig.KEY_LOGIN_ACTION_DATA);
    const actionData: ActionData = actionDataJson ? JSON.parse(actionDataJson) : {};

    switch (actionType) {
      case ActionType.PURCHASE_PRODUCT:
        return afterLoginPurchase(
          actionData as PurchaseActionData,
          checkout,
          language,
          trafficSource,
          storedSessions,
          i18nNavigate,
          name,
          isLoggedIn,
        );

      case ActionType.PURCHASE_VC_BUNDLE:
        const modal = await purchaseVcBundle(language, gameId);
        return modal === undefined; // If defined things went wrong.

      // Deprecated, use ActionType.REDIRECT instead.
      case ActionType.SUCCESS_PAGE:
        const successActionData = actionData as SuccessActionData;
        navigate(`${successActionData.successPath}${successActionData.successHash}`);
        return true;

      case ActionType.REDIRECT:
        const redirectActionData = actionData as RedirectActionData;
        navigate(redirectActionData.redirectPath);
        return true;
    }
  }, [
    checkout,
    language,
    trafficSource,
    storedSessions,
    i18nNavigate,
    name,
    isLoggedIn,
    modal,
    logout,
    login,
    loading,
  ]);

  return <context.Provider value={{ performLoginAction, saveLoginAction }}>{children}</context.Provider>;
};
